
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info, AlertCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RecentAlert } from "@/hooks/useRecentAlerts";

interface RecentAlertsProps {
  alerts: RecentAlert[];
  loading: boolean;
  onViewAllClick: () => void;
}

export const RecentAlerts: React.FC<RecentAlertsProps> = ({ 
  alerts, 
  loading, 
  onViewAllClick 
}) => {
  const navigate = useNavigate();

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case "danger":
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case "info":
        return <Info className="w-4 h-4 text-blue-600" />;
      default:
        return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  const getAlertBadgeColor = (type: string) => {
    switch (type) {
      case "warning":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
      case "danger":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      case "info":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  const handleAlertClick = (alert: RecentAlert) => {
    // Redirection intelligente selon la source de l'alerte
    switch (alert.source) {
      case "internal_message":
        // Rediriger vers la gestion des messages internes
        navigate("/gestion/messages");
        break;
      case "time_entry":
        // Rediriger vers les rapports pour voir les entrées de temps
        navigate("/reports?tab=dashboard");
        break;
      case "absence":
        // Rediriger vers la gestion des utilisateurs
        navigate("/gestion/users");
        break;
      default:
        // Par défaut, aller aux alertes dans les rapports
        navigate("/reports?tab=alerts");
        break;
    }
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <AlertTriangle className="w-5 h-5 mr-2 text-yellow-600" />
            Alertes Récentes
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-20">
          <Loader2 className="h-6 w-6 text-[#BD1E28] animate-spin" />
          <span className="ml-2">Chargement...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center text-lg">
          <AlertTriangle className="w-5 h-5 mr-2 text-yellow-600" />
          Alertes Récentes
        </CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onViewAllClick}
          className="text-[#BD1E28] border-[#BD1E28] hover:bg-[#BD1E28] hover:text-white"
        >
          Voir tout
        </Button>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <div className="text-center py-4">
            <Info className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Aucune alerte récente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div 
                key={alert.id} 
                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={() => handleAlertClick(alert)}
              >
                {getAlertIcon(alert.type)}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {alert.title}
                  </h4>
                  <p className="text-xs text-gray-600 mt-1 truncate">
                    {alert.description}
                  </p>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  <Badge className={getAlertBadgeColor(alert.type)}>
                    {alert.type === "warning" ? "Attention" :
                     alert.type === "danger" ? "Urgent" : "Info"}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {alert.severity === "high" ? "Élevé" :
                     alert.severity === "medium" ? "Moyen" : "Faible"}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
